import {
    compressSnapshotMapForSave,
    isValidSnapshotDataUrl,
    normalizePersistedSnapshotUrl,
} from '../../../../../../helpers/layoutSnapshotCapture';
import { ONE_DRAW_DEFAULT_MAXFILL_CATEGORY } from '../../../../../../services/layoutVariantPrompts';
import {
    getSalesforceIdForAiCache,
    loadLocalAiResultCacheWithFallback,
    saveLocalAiResultCache,
} from '../../../../../../services/geminiService';
import { useOneButtonStore } from '../../../../../../store/oneButtonStore';
import { useViewerStore } from '../../../../../../store/store';
import type { SavedRoofModulesLayout } from '../../../../../../@types/types';
import type { CategoryLayoutCandidate } from './categoryBestVariantScoring';
import { getCategoryMainLayoutKey, getLayoutCategoryNames } from './categoryBestVariantScoring';
import { canonicalCategoryName } from './resolveRoofCombinationsForRunAi';
import { applyCategoryBestLayoutsAction } from '../../dashboardActionCategoryBestVariants';
import { resolveRoofIdsForLayoutKey } from '../layout/resolveRoofIdsForLayoutKey';
import { countModulesInLayout } from './aiDesignFlowDebug';
import { resolveAltLayoutVariantMetrics, resolveAltLayoutVariantLayoutKey, type AltLayoutVariantRow } from './filterAltLayoutDisplayVariants';
import {
    enrichModulesLayoutWithStableRoofKeys,
} from '../layout/remapModulesLayoutRoofFaceIds';

export type AiScenariosCacheBlob = {
    aiModuleFillingScenarios?: unknown[];
    aiModuleFillingLayoutVariants?: unknown[];
    aiCategoryLayoutVariants?: Record<string, unknown[]>;
    layoutProductionByKey?: Record<string, number>;
    layoutModulesByKey?: Record<string, number>;
    layoutMetricsByKey?: Record<string, unknown>;
    /** Exact module placements keyed by layout / category-main keys — required to reopen 3D from cache. */
    layoutModulesLayoutByKey?: Record<string, SavedRoofModulesLayout>;
    categoryMainLayoutByCategory?: Record<string, CategoryLayoutCandidate>;
    aiResolvedRoofCombinations?: Array<{ name: string; roofFaceIds: string[] }>;
};

function hasModulesLayout(layout: SavedRoofModulesLayout | null | undefined): layout is SavedRoofModulesLayout {
    return Array.isArray(layout) && layout.length > 0;
}

function layoutMatchesExpectedModuleCount(
    layout: SavedRoofModulesLayout | null | undefined,
    expectedModules?: number | null,
): layout is SavedRoofModulesLayout {
    if (!hasModulesLayout(layout)) return false;
    if (expectedModules == null || expectedModules <= 0) return true;
    return countModulesInLayout(layout) === expectedModules;
}

/** Resolve a non-empty placement snapshot from any of the supplied layout keys. */
export function pickModulesLayoutByKeys(
    layoutModulesLayoutByKey: Record<string, SavedRoofModulesLayout | undefined> | undefined,
    keys: Array<string | undefined>,
    expectedModules?: number | null,
): SavedRoofModulesLayout | undefined {
    if (!layoutModulesLayoutByKey) return undefined;
    for (const key of keys) {
        if (!key) continue;
        const layout = layoutModulesLayoutByKey[key];
        if (layoutMatchesExpectedModuleCount(layout, expectedModules)) return layout;
    }

    if (expectedModules != null && expectedModules > 0) {
        const categoryLabels = keys.filter(
            (key): key is string =>
                Boolean(key) &&
                !key.startsWith('__category_main__:') &&
                !key.startsWith('variant:'),
        );
        for (const category of categoryLabels) {
            for (const [layoutKey, layout] of Object.entries(layoutModulesLayoutByKey)) {
                if (!layoutKey.startsWith(`variant:${category}:`)) continue;
                if (layoutMatchesExpectedModuleCount(layout, expectedModules)) return layout;
            }
        }
    }
    return undefined;
}

/** Prefer the current snapshot when it matches the card; otherwise search keyed layouts. */
export function resolveModulesLayoutMatchingCount(
    layoutModulesLayoutByKey: Record<string, SavedRoofModulesLayout | undefined> | undefined,
    keys: Array<string | undefined>,
    expectedModules: number | null | undefined,
    current?: SavedRoofModulesLayout | null,
): SavedRoofModulesLayout | undefined {
    if (layoutMatchesExpectedModuleCount(current, expectedModules)) return current;
    if (expectedModules == null || expectedModules <= 0) {
        return pickModulesLayoutByKeys(layoutModulesLayoutByKey, keys);
    }
    return pickModulesLayoutByKeys(layoutModulesLayoutByKey, keys, expectedModules);
}

/**
 * Rebuild `layoutModulesLayoutByKey` from every placement snapshot embedded in a cached
 * AI scenarios blob. Older cache rows only stored metrics on category winners and never
 * persisted the dedicated layout map, which made non-default 3D opens show an empty roof.
 */
export function rebuildLayoutModulesLayoutByKeyFromBlob(
    blob: AiScenariosCacheBlob,
): Record<string, SavedRoofModulesLayout> {
    const out: Record<string, SavedRoofModulesLayout> = {};

    const assign = (key: string | undefined, layout: SavedRoofModulesLayout | undefined) => {
        if (!key || !hasModulesLayout(layout) || hasModulesLayout(out[key])) return;
        out[key] = layout;
    };

    for (const [key, layout] of Object.entries(blob.layoutModulesLayoutByKey ?? {})) {
        assign(key, layout);
    }

    for (const [category, candidate] of Object.entries(blob.categoryMainLayoutByCategory ?? {})) {
        const catKey = getCategoryMainLayoutKey(category);
        assign(catKey, candidate?.modulesLayout);
        assign(candidate?.key, candidate?.modulesLayout);
        assign(category, candidate?.modulesLayout);
    }

    for (const [category, variants] of Object.entries(blob.aiCategoryLayoutVariants ?? {})) {
        for (const variant of variants ?? []) {
            const row = variant as {
                id?: string;
                label?: string;
                modules?: number;
                modulesLayout?: SavedRoofModulesLayout;
            };
            if (!hasModulesLayout(row.modulesLayout)) continue;
            assign(`variant:${category}:${row.id ?? row.label ?? row.modules}`, row.modulesLayout);
            assign(category, row.modulesLayout);
        }
    }

    return out;
}

/** Ensure each category winner carries the exact placement from the rebuilt layout map. */
function enrichCategoryMainLayoutsWithModulePlacements(
    categoryMainLayoutByCategory: Record<string, CategoryLayoutCandidate>,
    layoutModulesLayoutByKey: Record<string, SavedRoofModulesLayout>,
): Record<string, CategoryLayoutCandidate> {
    const out: Record<string, CategoryLayoutCandidate> = { ...categoryMainLayoutByCategory };
    for (const [category, candidate] of Object.entries(out)) {
        const expectedModules = candidate.modules > 0 ? candidate.modules : undefined;
        const catKey = getCategoryMainLayoutKey(category);
        const variantKey = candidate.variantId
            ? resolveAltLayoutVariantLayoutKey(category, { id: candidate.variantId })
            : undefined;
        const modulesLayout = resolveModulesLayoutMatchingCount(
            layoutModulesLayoutByKey,
            [catKey, candidate?.key, category, variantKey],
            expectedModules,
            candidate?.modulesLayout,
        );
        if (!modulesLayout) continue;
        out[category] = { ...candidate, modulesLayout };
    }
    return out;
}

function normalizeAiScenariosBlob(raw: unknown): AiScenariosCacheBlob {
    if (!raw) return {};
    if (typeof raw === 'string') {
        try {
            return JSON.parse(raw) as AiScenariosCacheBlob;
        } catch {
            return {};
        }
    }
    if (typeof raw === 'object') return raw as AiScenariosCacheBlob;
    return {};
}

/** Layout maps may key rows by `__category_main__:<cat>`, category label, or Gemini combo name. */
export function layoutKeysForCategory(
    category: string,
    roofCombinationsFromAi?: Array<{ name: string; roofFaceIds?: string[] }>,
): string[] {
    const catKey = getCategoryMainLayoutKey(category);
    const comboNames = (roofCombinationsFromAi ?? [])
        .filter((row) => canonicalCategoryName(row.name) === canonicalCategoryName(category))
        .map((row) => row.name)
        .filter(Boolean);
    return [...new Set([catKey, category, ...comboNames])];
}

export function resolveCategoryNumericFromMaps(
    category: string,
    byKey: Record<string, number | undefined> | null | undefined,
    roofCombinationsFromAi?: Array<{ name: string; roofFaceIds?: string[] }>,
): number {
    for (const key of layoutKeysForCategory(category, roofCombinationsFromAi)) {
        const value = byKey?.[key];
        if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
            return value;
        }
    }
    return 0;
}

function normalizeLayoutMapsForCategoryCards(blob: AiScenariosCacheBlob): {
    layoutProductionByKey: Record<string, number>;
    layoutModulesByKey: Record<string, number>;
    layoutMetricsByKey: Record<string, unknown>;
} {
    const layoutProductionByKey = {
        ...((blob.layoutProductionByKey ?? {}) as Record<string, number>),
    };
    const layoutModulesByKey = {
        ...((blob.layoutModulesByKey ?? {}) as Record<string, number>),
    };
    const layoutMetricsByKey = {
        ...((blob.layoutMetricsByKey ?? {}) as Record<string, unknown>),
    };

    for (const category of getLayoutCategoryNames()) {
        const catKey = getCategoryMainLayoutKey(category);
        const production = resolveCategoryNumericFromMaps(
            category,
            layoutProductionByKey,
            blob.aiResolvedRoofCombinations,
        );
        const modules = resolveCategoryNumericFromMaps(
            category,
            layoutModulesByKey,
            blob.aiResolvedRoofCombinations,
        );
        if (production > 0) layoutProductionByKey[catKey] = layoutProductionByKey[catKey] ?? production;
        if (modules > 0) layoutModulesByKey[catKey] = layoutModulesByKey[catKey] ?? modules;
        if (!layoutMetricsByKey[catKey]) {
            const metricRow = layoutKeysForCategory(category, blob.aiResolvedRoofCombinations)
                .map((key) => layoutMetricsByKey[key])
                .find((row) => row && typeof row === 'object');
            if (metricRow) layoutMetricsByKey[catKey] = metricRow;
        }
    }

    return { layoutProductionByKey, layoutModulesByKey, layoutMetricsByKey };
}

export function blobHasLayoutCatalogData(blob: AiScenariosCacheBlob): boolean {
    if (hasSimulatedLayoutCatalogData(blob.layoutProductionByKey, blob.layoutModulesByKey)) {
        return true;
    }
    if (Object.keys(blob.aiCategoryLayoutVariants ?? {}).length > 0) {
        return true;
    }
    if (hasStoredCategoryMainLayoutsInBlob(blob)) {
        return true;
    }
    const metrics = (blob.layoutMetricsByKey ?? {}) as Record<string, unknown>;
    return getLayoutCategoryNames().some((category) => {
        for (const key of layoutKeysForCategory(category, blob.aiResolvedRoofCombinations)) {
            if (metrics[key] && typeof metrics[key] === 'object') return true;
        }
        return false;
    });
}

/** True when a raw aiScenarios payload (object or JSON) contains a multi-layout catalog. */
export function hasAiScenariosLayoutCatalogData(raw: unknown): boolean {
    if (raw == null) return false;
    return blobHasLayoutCatalogData(normalizeAiScenariosBlob(raw));
}

/** True when aiScenarios from DB or IndexedDB is worth hydrating (not an empty placeholder). */
export function hasMeaningfulAiScenariosPayload(raw: unknown): boolean {
    if (raw == null) return false;
    if (hasAiScenariosLayoutCatalogData(raw)) return true;
    const blob = normalizeAiScenariosBlob(raw);
    if (Array.isArray(blob.aiModuleFillingScenarios) && blob.aiModuleFillingScenarios.length > 0) {
        return true;
    }
    return hasStoredCategoryMainLayoutsInBlob(blob);
}

/** True when the blob already has persisted per-category main-card winners (not just default maxfill). */
export function hasStoredCategoryMainLayoutsInBlob(blob: AiScenariosCacheBlob | null | undefined): boolean {
    const main = (blob?.categoryMainLayoutByCategory ?? {}) as Record<string, CategoryLayoutCandidate>;
    const hasStoredWinners = getLayoutCategoryNames()
        .filter((category) => category !== ONE_DRAW_DEFAULT_MAXFILL_CATEGORY)
        .some((category) => {
            const row = main[category];
            return Boolean(
                row &&
                    (row.production ?? 0) > 0 &&
                    (row.modules ?? 0) > 0 &&
                    (row.roofIds?.length ?? 0) > 0,
            );
        });
    if (hasStoredWinners) return true;

    return getLayoutCategoryNames()
        .filter((category) => category !== ONE_DRAW_DEFAULT_MAXFILL_CATEGORY)
        .some((category) => {
            const production = resolveCategoryNumericFromMaps(
                category,
                blob?.layoutProductionByKey,
                blob?.aiResolvedRoofCombinations,
            );
            const modules = resolveCategoryNumericFromMaps(
                category,
                blob?.layoutModulesByKey,
                blob?.aiResolvedRoofCombinations,
            );
            if (!(production > 0) || !(modules > 0)) return false;
            const metrics = (blob?.layoutMetricsByKey ?? {}) as Record<string, unknown>;
            return layoutKeysForCategory(category, blob?.aiResolvedRoofCombinations).some(
                (key) => metrics[key] && typeof metrics[key] === 'object',
            );
        });
}

/**
 * True when the in-memory store already has a multi-category AI catalog.
 * Default-only production (e.g. live maxfill on scene load) must not block cache hydration.
 */
export function hasHydratedAiLayoutCatalogInStore(state?: any): boolean {
    const s = state ?? (useViewerStore.getState() as any);
    const main = (s.categoryMainLayoutByCategory ?? {}) as Record<string, CategoryLayoutCandidate>;
    const hasNonDefaultCategoryWinner = getLayoutCategoryNames()
        .filter((category) => category !== ONE_DRAW_DEFAULT_MAXFILL_CATEGORY)
        .some((category) => {
            const row = main[category];
            return Boolean(row && (row.production ?? 0) > 0 && (row.modules ?? 0) > 0);
        });
    if (hasNonDefaultCategoryWinner) return true;

    const variantCategories = Object.keys(s.aiCategoryLayoutVariants ?? {}).filter(
        (category) => (s.aiCategoryLayoutVariants?.[category]?.length ?? 0) > 0,
    );
    return (
        variantCategories.length > 0 &&
        hasSimulatedLayoutCatalogData(s.layoutProductionByKey, s.layoutModulesByKey) &&
        Object.keys(main).length > 0
    );
}

export function hasSimulatedLayoutCatalogData(
    layoutProductionByKey: Record<string, number | undefined> | null | undefined,
    layoutModulesByKey?: Record<string, number | undefined> | null,
): boolean {
    const prodEntries = Object.values(layoutProductionByKey ?? {});
    if (prodEntries.some((v) => typeof v === 'number' && Number.isFinite(v) && v > 0)) {
        return true;
    }
    const modEntries = Object.values(layoutModulesByKey ?? {});
    return modEntries.some((v) => typeof v === 'number' && Number.isFinite(v) && v > 0);
}

/** True when the viewer store has layout rows to show even if Gemini returned nothing. */
export function hasUsableAiDesignCatalogInStore(state?: any): boolean {
    const s = state ?? (useViewerStore.getState() as any);
    return (
        hasHydratedAiLayoutCatalogInStore(s) ||
        hasSimulatedLayoutCatalogData(s.layoutProductionByKey, s.layoutModulesByKey)
    );
}

/**
 * Older cache rows stored card metrics (`layoutProductionByKey` / `layoutModulesByKey`) but not
 * `categoryMainLayoutByCategory`. Rebuild minimal winner records so 3D apply can resolve roof ids.
 */
function rebuildCategoryMainLayoutFromMetrics(
    blob: AiScenariosCacheBlob,
    layoutModulesLayoutByKey: Record<string, SavedRoofModulesLayout>,
): Record<string, CategoryLayoutCandidate> {
    const out: Record<string, CategoryLayoutCandidate> = {
        ...((blob.categoryMainLayoutByCategory ?? {}) as Record<string, CategoryLayoutCandidate>),
    };
    const metricsByKey = (blob.layoutMetricsByKey ?? {}) as Record<
        string,
        {
            efficiency?: number | null;
            solarAccess?: number | null;
            multiplier?: number | null;
            treeCount?: number | null;
            roofCount?: number | null;
        }
    >;

    for (const category of getLayoutCategoryNames()) {
        const catKey = getCategoryMainLayoutKey(category);
        const production = resolveCategoryNumericFromMaps(
            category,
            blob.layoutProductionByKey,
            blob.aiResolvedRoofCombinations,
        );
        const modules = resolveCategoryNumericFromMaps(
            category,
            blob.layoutModulesByKey,
            blob.aiResolvedRoofCombinations,
        );
        if (!(production > 0) || !(modules > 0)) continue;

        const combo = (blob.aiResolvedRoofCombinations ?? []).find(
            (row) => canonicalCategoryName(row.name) === canonicalCategoryName(category),
        );
        const variantRoofIds = ((blob.aiCategoryLayoutVariants?.[category] ?? []) as Array<{ roofIds?: string[] }>)
            .flatMap((variant) => (Array.isArray(variant.roofIds) ? variant.roofIds.filter(Boolean) : []));
        const roofIds =
            (out[category]?.roofIds?.length ? out[category].roofIds : null) ??
            combo?.roofFaceIds?.filter(Boolean) ??
            (variantRoofIds.length ? [...new Set(variantRoofIds)] : []) ??
            [];
        if (!roofIds.length) continue;

        const metrics =
            layoutKeysForCategory(category, blob.aiResolvedRoofCombinations)
                .map((key) => metricsByKey[key])
                .find((row) => row && typeof row === 'object') ?? metricsByKey[catKey] ?? metricsByKey[category];
        const variantKey = out[category]?.variantId
            ? resolveAltLayoutVariantLayoutKey(category, { id: out[category].variantId })
            : undefined;
        const modulesLayout = resolveModulesLayoutMatchingCount(
            layoutModulesLayoutByKey,
            [catKey, out[category]?.key, category, combo?.name, variantKey],
            modules,
            out[category]?.modulesLayout,
        );

        out[category] = {
            key: catKey,
            source: out[category]?.source ?? 'layoutKey',
            variantId: out[category]?.variantId,
            label: out[category]?.label,
            displayLabel: out[category]?.displayLabel,
            roofIds,
            dir: out[category]?.dir ?? null,
            minEff: out[category]?.minEff ?? null,
            production,
            modules,
            efficiency:
                typeof metrics?.efficiency === 'number' ? metrics.efficiency : out[category]?.efficiency ?? null,
            solarAccess:
                typeof metrics?.solarAccess === 'number' ? metrics.solarAccess : out[category]?.solarAccess ?? null,
            multiplier:
                typeof metrics?.multiplier === 'number' ? metrics.multiplier : out[category]?.multiplier ?? null,
            treeCount: metrics?.treeCount ?? out[category]?.treeCount ?? null,
            roofCount: metrics?.roofCount ?? out[category]?.roofCount ?? roofIds.length,
            modulesLayout: out[category]?.modulesLayout?.length
                ? out[category].modulesLayout
                : modulesLayout,
        };
    }

    return out;
}

/** Drop in-memory wishlist and strip wishlist from IndexedDB before a fresh AI run. */
export function clearWishlistCacheForRun(): void {
    const store = useOneButtonStore.getState();
    store.setSavedAIDesigns([]);
    store.setActiveAIDesignId(null);

    void (async () => {
        try {
            const sfId = getSalesforceIdForAiCache();
            const local = await loadLocalAiResultCacheWithFallback(sfId);
            if (!local) return;
            if (!Array.isArray(local.aiWishlist) || local.aiWishlist.length === 0) return;
            await saveLocalAiResultCache({ ...local, aiWishlist: [] }, sfId);
        } catch {
            // best-effort local cache cleanup
        }
    })();
}

function enrichModulesLayoutRecordWithViewer(
    viewer: { roofMeshArr?: Array<{ roofFaceId?: string; userData?: Record<string, unknown> }> } | null | undefined,
    record: Record<string, SavedRoofModulesLayout | undefined> | undefined,
): Record<string, SavedRoofModulesLayout> {
    const out: Record<string, SavedRoofModulesLayout> = {};
    for (const [key, layout] of Object.entries(record ?? {})) {
        if (!hasModulesLayout(layout)) continue;
        out[key] = enrichModulesLayoutWithStableRoofKeys(viewer, layout);
    }
    return out;
}

function enrichCategoryMainLayoutsWithViewer(
    viewer: { roofMeshArr?: Array<{ roofFaceId?: string; userData?: Record<string, unknown> }> } | null | undefined,
    byCategory: Record<string, CategoryLayoutCandidate>,
): Record<string, CategoryLayoutCandidate> {
    const out: Record<string, CategoryLayoutCandidate> = {};
    for (const [category, candidate] of Object.entries(byCategory)) {
        if (!hasModulesLayout(candidate?.modulesLayout)) {
            out[category] = candidate;
            continue;
        }
        const modulesLayout = enrichModulesLayoutWithStableRoofKeys(viewer, candidate.modulesLayout);
        out[category] = { ...candidate, modulesLayout };
    }
    return out;
}

/** Compact blob for IndexedDB: placements once in `layoutModulesLayoutByKey`, metrics-only variant rows. */
export function buildAiScenariosCacheBlobForLocalPersist(): AiScenariosCacheBlob {
    const state = useViewerStore.getState() as any;
    const viewer = state.sceneViewer as {
        roofMeshArr?: Array<{ roofFaceId?: string; userData?: Record<string, unknown> }>;
    } | null;
    const layoutModulesLayoutByKey = enrichModulesLayoutRecordWithViewer(
        viewer,
        state.layoutModulesLayoutByKey ?? {},
    );

    const aiCategoryLayoutVariants: Record<string, unknown[]> = {};
    for (const [category, variants] of Object.entries(
        (state.aiCategoryLayoutVariants ?? {}) as Record<string, unknown[]>,
    )) {
        aiCategoryLayoutVariants[category] = (variants ?? []).map((variant) => {
            const row = variant as AltLayoutVariantRow & { id?: string };
            const variantKey = resolveAltLayoutVariantLayoutKey(category, row);
            const layoutFromRow = hasModulesLayout(row.modulesLayout) ? row.modulesLayout : undefined;
            const layoutFromMap = layoutModulesLayoutByKey[variantKey];
            const modulesLayout = layoutFromRow ?? (hasModulesLayout(layoutFromMap) ? layoutFromMap : undefined);
            if (hasModulesLayout(modulesLayout)) {
                const enriched = enrichModulesLayoutWithStableRoofKeys(viewer, modulesLayout);
                layoutModulesLayoutByKey[variantKey] = enriched;
            }
            const stripped = stripEmbeddedSnapshotFields(row) as Record<string, unknown>;
            const { modulesLayout: _omitLayout, ...rest } = stripped;
            const moduleCount = hasModulesLayout(modulesLayout)
                ? countModulesInLayout(modulesLayout)
                : asPositiveNumberFromRow(rest.modules);
            return moduleCount > 0 ? { ...rest, modules: moduleCount } : rest;
        });
    }

    const categoryMainLayoutByCategory: Record<string, CategoryLayoutCandidate> = {};
    const enrichedMain = enrichCategoryMainLayoutsWithViewer(viewer, state.categoryMainLayoutByCategory ?? {});
    for (const [category, candidate] of Object.entries(enrichedMain)) {
        const catKey = getCategoryMainLayoutKey(category);
        if (hasModulesLayout(candidate?.modulesLayout) && !hasModulesLayout(layoutModulesLayoutByKey[catKey])) {
            layoutModulesLayoutByKey[catKey] = candidate.modulesLayout;
        }
        const stripped = stripEmbeddedSnapshotFields(candidate) as CategoryLayoutCandidate & {
            modulesLayout?: SavedRoofModulesLayout;
        };
        const { modulesLayout: _omitLayout, ...rest } = stripped;
        categoryMainLayoutByCategory[category] = rest as CategoryLayoutCandidate;
    }

    return sanitizeAiScenariosCacheBlobForSave({
        aiModuleFillingScenarios: state.aiModuleFillingScenarios ?? [],
        aiModuleFillingLayoutVariants: state.aiModuleFillingLayoutVariants ?? [],
        aiCategoryLayoutVariants,
        layoutProductionByKey: state.layoutProductionByKey ?? {},
        layoutModulesByKey: state.layoutModulesByKey ?? {},
        layoutMetricsByKey: state.layoutMetricsByKey ?? {},
        layoutModulesLayoutByKey,
        categoryMainLayoutByCategory,
        aiResolvedRoofCombinations: state.aiResolvedRoofCombinations ?? [],
    });
}

function asPositiveNumberFromRow(value: unknown): number {
    return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : 0;
}

export type SaveAiResultPayloadFields = {
    layoutSnapshots?: string;
    variantSnapshots?: string;
    aiScenarios: string;
    treeCuttingScenarios: string;
};

const EMBEDDED_SNAPSHOT_FIELD_KEYS = [
    'snapshotUrl',
    'layoutSnapshotUrl',
    'snapshotCaptureError',
    'snapshotError',
] as const;

/** Drop inline base64 thumbnails — they belong in layoutSnapshots / variantSnapshots maps. */
function stripEmbeddedSnapshotFields<T>(row: T): T {
    if (!row || typeof row !== 'object') return row;
    const rest = { ...(row as Record<string, unknown>) };
    for (const key of EMBEDDED_SNAPSHOT_FIELD_KEYS) {
        delete rest[key];
    }
    return rest as T;
}

/** Drop per-scenario base64 thumbnails — they belong in layoutSnapshots, not the scenarios JSON. */
function stripEmbeddedScenarioSnapshots<T>(scenarios: T[] | null | undefined): T[] {
    if (!Array.isArray(scenarios)) return [];
    return scenarios.map((scenario) => stripEmbeddedSnapshotFields(scenario));
}

function stripEmbeddedSnapshotFieldsFromList<T>(rows: T[] | null | undefined): T[] {
    if (!Array.isArray(rows)) return [];
    return rows.map((row) => stripEmbeddedSnapshotFields(row));
}

function stripEmbeddedSnapshotFieldsFromVariantsByCategory(
    byCategory: Record<string, unknown[]> | null | undefined,
): Record<string, unknown[]> {
    const out: Record<string, unknown[]> = {};
    for (const [category, variants] of Object.entries(byCategory ?? {})) {
        out[category] = stripEmbeddedSnapshotFieldsFromList(variants);
    }
    return out;
}

function stripEmbeddedSnapshotFieldsFromCategoryMain(
    byCategory: Record<string, CategoryLayoutCandidate> | null | undefined,
): Record<string, CategoryLayoutCandidate> {
    const out: Record<string, CategoryLayoutCandidate> = {};
    for (const [category, candidate] of Object.entries(byCategory ?? {})) {
        out[category] = stripEmbeddedSnapshotFields(candidate);
    }
    return out;
}

function stripSnapshotsFromTreeScenariosByLayoutKey(
    byKey: Record<string, unknown[]> | null | undefined,
): Record<string, unknown[]> {
    const out: Record<string, unknown[]> = {};
    for (const [layoutKey, scenarios] of Object.entries(byKey ?? {})) {
        out[layoutKey] = stripEmbeddedScenarioSnapshots(scenarios);
    }
    return out;
}

const TREE_CUT_SNAPSHOT_PREFIX = '__tree_cut__:';

function treeCutSnapshotSidecarKey(layoutKey: string, scenarioIndex: number): string {
    return `${TREE_CUT_SNAPSHOT_PREFIX}${JSON.stringify({ layoutKey, scenarioIndex })}`;
}

function parseTreeCutSnapshotSidecarKey(
    key: string,
): { layoutKey: string; scenarioIndex: number } | null {
    if (!key.startsWith(TREE_CUT_SNAPSHOT_PREFIX)) return null;
    try {
        const parsed = JSON.parse(key.slice(TREE_CUT_SNAPSHOT_PREFIX.length)) as {
            layoutKey?: string;
            scenarioIndex?: number;
        };
        const layoutKey = typeof parsed.layoutKey === 'string' ? parsed.layoutKey : '';
        const scenarioIndex = parsed.scenarioIndex;
        if (!layoutKey || !Number.isInteger(scenarioIndex) || scenarioIndex < 0) return null;
        return { layoutKey, scenarioIndex };
    } catch {
        return null;
    }
}

/** Move tree-cutting thumbnails out of scenario rows into the layoutSnapshots sidecar map. */
function extractTreeCutSnapshotsFromScenarios(
    byLayoutKey: Record<string, unknown[]> | null | undefined,
): Record<string, string> {
    const out: Record<string, string> = {};
    for (const [layoutKey, scenarios] of Object.entries(byLayoutKey ?? {})) {
        if (!Array.isArray(scenarios)) continue;
        scenarios.forEach((scenario, index) => {
            if (!scenario || typeof scenario !== 'object') return;
            const row = scenario as Record<string, unknown>;
            const direct =
                typeof row.snapshotUrl === 'string' && isValidSnapshotDataUrl(row.snapshotUrl)
                    ? row.snapshotUrl
                    : undefined;
            const layout =
                typeof row.layoutSnapshotUrl === 'string' && isValidSnapshotDataUrl(row.layoutSnapshotUrl)
                    ? row.layoutSnapshotUrl
                    : undefined;
            const url = direct ?? layout;
            if (url) {
                out[treeCutSnapshotSidecarKey(layoutKey, index)] = url;
            }
        });
    }
    return out;
}

/** Re-attach tree-cutting thumbnails from layoutSnapshots onto scenario rows after load. */
export function rehydrateTreeCutScenariosFromLayoutSnapshotMap(
    byLayoutKey: Record<string, unknown[]> | null | undefined,
    layoutSnapshots: Record<string, string>,
): Record<string, unknown[]> {
    const out: Record<string, unknown[]> = { ...(byLayoutKey ?? {}) };
    for (const [key, url] of Object.entries(layoutSnapshots)) {
        const parsed = parseTreeCutSnapshotSidecarKey(key);
        if (!parsed || !isValidSnapshotDataUrl(url)) continue;
        const scenarios = out[parsed.layoutKey];
        if (!Array.isArray(scenarios) || parsed.scenarioIndex >= scenarios.length) continue;
        const scenario = scenarios[parsed.scenarioIndex];
        if (!scenario || typeof scenario !== 'object') continue;
        const nextScenarios = [...scenarios];
        nextScenarios[parsed.scenarioIndex] = {
            ...(scenario as Record<string, unknown>),
            snapshotUrl: url,
        };
        out[parsed.layoutKey] = nextScenarios;
    }
    return out;
}

function filterLayoutSnapshotsToCategoryMainsOnly(
    layoutMap: Record<string, string | undefined> | null | undefined,
): Record<string, string> {
    const out: Record<string, string> = {};
    for (const [key, value] of Object.entries(layoutMap ?? {})) {
        if (!key.startsWith('__category_main__:')) continue;
        if (typeof value === 'string' && value.trim().length > 0) {
            out[key] = value;
        }
    }
    return out;
}

function buildLayoutSnapshotMapForSave(
    state: {
        layoutSnapshotsByKey?: Record<string, string | undefined>;
        aiModuleFillingTreeScenariosByLayoutKey?: Record<string, unknown[]>;
    },
    includeSnapshotsOnSave: boolean,
): Record<string, string | undefined> {
    if (includeSnapshotsOnSave) {
        return {
            ...(state.layoutSnapshotsByKey ?? {}),
            ...extractTreeCutSnapshotsFromScenarios(state.aiModuleFillingTreeScenariosByLayoutKey),
        };
    }
    return filterLayoutSnapshotsToCategoryMainsOnly(state.layoutSnapshotsByKey);
}

/** Remove duplicated snapshot payloads from the AI scenarios cache blob before JSON persistence. */
export function sanitizeAiScenariosCacheBlobForSave(blob: AiScenariosCacheBlob): AiScenariosCacheBlob {
    return {
        ...blob,
        aiModuleFillingScenarios: stripEmbeddedScenarioSnapshots(blob.aiModuleFillingScenarios),
        aiModuleFillingLayoutVariants: stripEmbeddedSnapshotFieldsFromList(blob.aiModuleFillingLayoutVariants),
        aiCategoryLayoutVariants: stripEmbeddedSnapshotFieldsFromVariantsByCategory(blob.aiCategoryLayoutVariants),
        categoryMainLayoutByCategory: stripEmbeddedSnapshotFieldsFromCategoryMain(blob.categoryMainLayoutByCategory),
    };
}

export function sanitizeTreeCuttingScenariosForSave(
    byKey: Record<string, unknown[]> | null | undefined,
): Record<string, unknown[]> {
    return stripSnapshotsFromTreeScenariosByLayoutKey(byKey);
}

function safeJsonStringify(value: unknown, label: string): string {
    try {
        return JSON.stringify(value);
    } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        throw new Error(`Could not serialize ${label} for save: ${message}`);
    }
}

/**
 * Build the remote SaveAiResult request body from viewer store state.
 * Strips embedded scenario snapshots to avoid duplicating megabytes of base64 in aiScenarios.
 * When images are included, snapshots are downscaled before JSON serialization to avoid OOM.
 */
export async function buildSaveAiResultPayloadFromStore(
    includeSnapshotsOnSave: boolean,
    onSnapshotCompressProgress?: (completed: number, total: number) => void,
): Promise<SaveAiResultPayloadFields> {
    const state = useViewerStore.getState() as any;
    const blob = buildAiScenariosCacheBlobForLocalPersist();
    const treeCutting = sanitizeTreeCuttingScenariosForSave(
        state.aiModuleFillingTreeScenariosByLayoutKey ?? {},
    );

    let layoutSnapshots: string | undefined;
    let variantSnapshots: string | undefined;
    const layoutMap = buildLayoutSnapshotMapForSave(state, includeSnapshotsOnSave);
    const layoutTotal = Object.keys(layoutMap).length;

    if (includeSnapshotsOnSave) {
        const variantMap = state.aiLayoutVariantSnapshots ?? {};
        const variantTotal = Object.keys(variantMap).length;
        const combinedTotal = layoutTotal + variantTotal;
        let combinedCompleted = 0;

        const reportCombinedProgress = (stepCompleted: number, stepTotal: number) => {
            if (!combinedTotal) return;
            const stepOffset = combinedCompleted;
            onSnapshotCompressProgress?.(stepOffset + stepCompleted, combinedTotal);
        };

        const compressedLayout = await compressSnapshotMapForSave(layoutMap, (done, total) => {
            reportCombinedProgress(done, total);
        });
        combinedCompleted = layoutTotal;

        const compressedVariant = await compressSnapshotMapForSave(variantMap, (done, total) => {
            reportCombinedProgress(done, total);
        });

        layoutSnapshots = safeJsonStringify(compressedLayout, 'layout snapshots');
        variantSnapshots = safeJsonStringify(compressedVariant, 'variant snapshots');
    } else if (layoutTotal > 0) {
        const compressedLayout = await compressSnapshotMapForSave(layoutMap, onSnapshotCompressProgress);
        layoutSnapshots = safeJsonStringify(compressedLayout, 'layout snapshots');
    }

    return {
        ...(layoutSnapshots ? { layoutSnapshots } : {}),
        ...(includeSnapshotsOnSave && variantSnapshots ? { variantSnapshots } : {}),
        aiScenarios: safeJsonStringify(blob, 'AI scenarios'),
        treeCuttingScenarios: safeJsonStringify(treeCutting, 'tree-cutting scenarios'),
    };
}

/** Sanitized + compressed payload for IndexedDB after a successful remote AI save. */
export async function buildLocalAiResultCachePayloadFromStore(aiWishlist: unknown[]): Promise<{
    layoutSnapshots: Record<string, string> | null;
    variantSnapshots: Record<string, string> | null;
    aiScenarios: AiScenariosCacheBlob;
    treeCuttingScenarios: Record<string, unknown[]>;
    aiWishlist: unknown[];
    aiResult: string | null;
}> {
    const state = useViewerStore.getState() as any;
    const layoutMapForLocal = buildLayoutSnapshotMapForSave(state, true);
    const [layoutSnapshots, variantSnapshots] = await Promise.all([
        compressSnapshotMapForSave(layoutMapForLocal),
        compressSnapshotMapForSave(state.aiLayoutVariantSnapshots ?? {}),
    ]);

    return {
        layoutSnapshots: Object.keys(layoutSnapshots).length ? layoutSnapshots : null,
        variantSnapshots: Object.keys(variantSnapshots).length ? variantSnapshots : null,
        aiScenarios: buildAiScenariosCacheBlobForLocalPersist(),
        treeCuttingScenarios: sanitizeTreeCuttingScenariosForSave(
            state.aiModuleFillingTreeScenariosByLayoutKey ?? {},
        ),
        aiWishlist,
        aiResult: typeof state.aiResult === 'string' ? state.aiResult : null,
    };
}

function coerceSnapshotEntry(value: unknown): string | undefined {
    if (value == null) return undefined;
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) return undefined;
        const direct = normalizePersistedSnapshotUrl(trimmed);
        if (direct) return direct;
        if ((trimmed.startsWith('{') || trimmed.startsWith('[')) && trimmed.length > 2) {
            try {
                return coerceSnapshotEntry(JSON.parse(trimmed));
            } catch {
                return undefined;
            }
        }
        return undefined;
    }
    if (typeof value === 'object') {
        const record = value as Record<string, unknown>;
        for (const key of [
            'snapshotUrl',
            'url',
            'data',
            'image',
            'Image',
            'SnapshotUrl',
            'layoutSnapshotUrl',
        ]) {
            const hit = coerceSnapshotEntry(record[key]);
            if (hit) return hit;
        }
    }
    return undefined;
}

function parseValidSnapshotMap(
    input: Record<string, unknown> | null | undefined,
): Record<string, string> {
    const out: Record<string, string> = {};
    for (const [key, value] of Object.entries(input ?? {})) {
        const normalized = coerceSnapshotEntry(value);
        if (normalized) out[key] = normalized;
    }
    return out;
}

/** Merge two snapshot sidecar maps, keeping only valid data URLs. */
export function mergeValidSnapshotMaps(
    base: Record<string, string | undefined> | Record<string, unknown> | null | undefined,
    incoming: Record<string, unknown> | null | undefined,
): Record<string, string> {
    const baseRecord =
        base && typeof base === 'object' ? (base as Record<string, unknown>) : undefined;
    return {
        ...parseValidSnapshotMap(baseRecord),
        ...parseValidSnapshotMap(incoming),
    };
}

/** True when a snapshot sidecar map contains at least one valid data URL. */
export function hasValidSnapshotMapData(
    map: Record<string, unknown> | null | undefined,
): boolean {
    return Object.keys(mergeValidSnapshotMaps(null, map)).length > 0;
}

function enrichCategoryLayoutVariantsWithSnapshots(
    variantsByCategory: Record<string, unknown[]>,
    variantSnapshots: Record<string, string>,
): Record<string, unknown[]> {
    const out: Record<string, unknown[]> = {};
    for (const [category, rows] of Object.entries(variantsByCategory ?? {})) {
        out[category] = (rows ?? []).map((row) => {
            const variant = row as VariantSnapshotRow;
            const embedded = normalizePersistedSnapshotUrl(variant.snapshotUrl);
            if (embedded) return { ...variant, snapshotUrl: embedded, snapshotCaptureError: undefined };
            const resolved = resolveVariantSnapshotFromSidecar(variant, variantSnapshots, {});
            return resolved
                ? { ...variant, snapshotUrl: resolved, snapshotCaptureError: undefined }
                : row;
        });
    }
    return out;
}

/**
 * Merge layout/variant snapshot sidecars into the live viewer store without re-applying
 * the full AI scenarios blob (which would strip embedded thumbnails and reset maps).
 */
export function mergeSnapshotSidecarsIntoViewerStore(args: {
    layoutSnapshots?: Record<string, unknown> | null;
    variantSnapshots?: Record<string, unknown> | null;
}): boolean {
    const incomingLayout = mergeValidSnapshotMaps(null, args.layoutSnapshots);
    const incomingVariant = mergeValidSnapshotMaps(null, args.variantSnapshots);
    if (!Object.keys(incomingLayout).length && !Object.keys(incomingVariant).length) {
        return false;
    }

    const state = useViewerStore.getState() as any;
    const mergedLayout = {
        ...parseValidSnapshotMap(state.layoutSnapshotsByKey as Record<string, unknown>),
        ...incomingLayout,
    };
    const mergedVariant = {
        ...parseValidSnapshotMap(state.aiLayoutVariantSnapshots as Record<string, unknown>),
        ...incomingVariant,
    };
    const aliasedLayout = aliasLayoutSnapshotKeys(mergedLayout, state.aiResolvedRoofCombinations ?? []);
    const categoryMainWithSnapshots = rehydrateCategoryMainSnapshotsFromSidecarMaps({
        layoutSnapshotsByKey: aliasedLayout,
        variantSnapshots: mergedVariant,
        categoryMainLayoutByCategory: (state.categoryMainLayoutByCategory ?? {}) as Record<
            string,
            CategoryLayoutCandidate
        >,
    });
    const enrichedVariants = enrichCategoryLayoutVariantsWithSnapshots(
        (state.aiCategoryLayoutVariants ?? {}) as Record<string, unknown[]>,
        mergedVariant,
    );
    const rehydratedTree = rehydrateTreeCutScenariosFromLayoutSnapshotMap(
        (state.aiModuleFillingTreeScenariosByLayoutKey ?? {}) as Record<string, unknown[]>,
        aliasedLayout,
    );

    useViewerStore.setState({
        layoutSnapshotsByKey: { ...aliasedLayout },
        aiLayoutVariantSnapshots: { ...mergedVariant },
        categoryMainLayoutByCategory: { ...categoryMainWithSnapshots },
        aiCategoryLayoutVariants: { ...enrichedVariants },
        aiModuleFillingTreeScenariosByLayoutKey: rehydratedTree,
        layoutSnapshotHydrationSeq: ((state.layoutSnapshotHydrationSeq as number | undefined) ?? 0) + 1,
    } as any);

    return true;
}

/** @deprecated Use mergeSnapshotSidecarsIntoViewerStore — kept for call-site compatibility. */
export function applyFetchedSnapshotSidecarsToViewerStore(args: {
    layoutSnapshots?: Record<string, unknown> | null;
    variantSnapshots?: Record<string, unknown> | null;
    aiDesignsDataSource?: 'cache' | 'db' | 'run';
}): boolean {
    return mergeSnapshotSidecarsIntoViewerStore({
        layoutSnapshots: args.layoutSnapshots,
        variantSnapshots: args.variantSnapshots,
    });
}

/** Copy layout thumbnails across category / combo / `__category_main__:` key aliases. */
function aliasLayoutSnapshotKeys(
    snapshots: Record<string, string>,
    roofCombinationsFromAi?: Array<{ name: string; roofFaceIds?: string[] }>,
): Record<string, string> {
    const out = { ...snapshots };
    for (const category of getLayoutCategoryNames()) {
        const keys = layoutKeysForCategory(category, roofCombinationsFromAi);
        let url: string | undefined;
        for (const key of keys) {
            const hit = out[key];
            if (typeof hit === 'string' && normalizePersistedSnapshotUrl(hit)) {
                url = normalizePersistedSnapshotUrl(hit);
                break;
            }
        }
        if (!url) continue;
        for (const key of keys) {
            if (!out[key]) out[key] = url;
        }
    }
    return out;
}

type VariantSnapshotRow = {
    id?: string;
    label?: string;
    roofIds?: string[];
    dir?: unknown;
    snapshotUrl?: string;
    productionW?: number;
    modules?: number;
};

function variantSnapshotMapKey(label: string, roofIds: string[], dir: unknown): string {
    return JSON.stringify({
        label,
        roofIds,
        dir: dir ?? null,
    });
}

function lookupVariantSnapshotInMap(
    variant: VariantSnapshotRow,
    variantSnapshots: Record<string, string>,
): string | undefined {
    const rawLabel = String(variant.label ?? '').trim();
    const roofIds = Array.isArray(variant.roofIds) ? variant.roofIds : [];
    const dir = variant.dir ?? null;
    const tryHit = (label: string, ids: string[]) => {
        const url = variantSnapshots[variantSnapshotMapKey(label, ids, dir)];
        return typeof url === 'string' && isValidSnapshotDataUrl(url) ? url : undefined;
    };

    const exact = tryHit(rawLabel, roofIds);
    if (exact) return exact;

    const sorted = tryHit(rawLabel, [...roofIds].sort());
    if (sorted) return sorted;

    const sortedTarget = [...roofIds].sort().join('\u0000');
    for (const [jsonKey, url] of Object.entries(variantSnapshots)) {
        if (typeof url !== 'string' || !isValidSnapshotDataUrl(url)) continue;
        try {
            const parsed = JSON.parse(jsonKey) as { roofIds?: string[]; dir?: unknown; label?: string };
            if (String(parsed.label ?? '').trim() !== rawLabel) continue;
            if ((parsed.dir ?? null) !== dir) continue;
            if ([...(parsed.roofIds || [])].sort().join('\u0000') !== sortedTarget) continue;
            return url;
        } catch {
            continue;
        }
    }
    return undefined;
}

function resolveVariantSnapshotFromSidecar(
    variant: VariantSnapshotRow,
    variantSnapshots: Record<string, string>,
    layoutSnapshots: Record<string, string>,
): string | undefined {
    const embedded =
        normalizePersistedSnapshotUrl(variant.snapshotUrl) ??
        undefined;
    if (embedded) return embedded;
    const fromVariantMap = lookupVariantSnapshotInMap(variant, variantSnapshots);
    if (fromVariantMap) return fromVariantMap;
    return undefined;
}

/** Re-attach thumbnails stripped from the aiScenarios blob onto category mains only (variants resolve at render). */
function rehydrateCategoryMainSnapshotsFromSidecarMaps(args: {
    layoutSnapshotsByKey: Record<string, string>;
    variantSnapshots: Record<string, string>;
    categoryMainLayoutByCategory: Record<string, CategoryLayoutCandidate>;
}): Record<string, CategoryLayoutCandidate> {
    const { layoutSnapshotsByKey, variantSnapshots, categoryMainLayoutByCategory } = args;

    const nextMain: Record<string, CategoryLayoutCandidate> = {};
    for (const [category, candidate] of Object.entries(categoryMainLayoutByCategory)) {
        const catKey = getCategoryMainLayoutKey(category);
        const snapshotUrl =
            resolveVariantSnapshotFromSidecar(
                {
                    label: candidate.label,
                    roofIds: candidate.roofIds,
                    dir: candidate.dir,
                    productionW: candidate.production,
                    modules: candidate.modules,
                    snapshotUrl: candidate.snapshotUrl,
                },
                variantSnapshots,
                layoutSnapshotsByKey,
            ) ??
            (normalizePersistedSnapshotUrl(layoutSnapshotsByKey[catKey])
                ? layoutSnapshotsByKey[catKey]
                : normalizePersistedSnapshotUrl(layoutSnapshotsByKey[category])
                  ? layoutSnapshotsByKey[category]
                  : normalizePersistedSnapshotUrl(layoutSnapshotsByKey[candidate.key])
                    ? layoutSnapshotsByKey[candidate.key]
                    : normalizePersistedSnapshotUrl(candidate.snapshotUrl));
        nextMain[category] =
            snapshotUrl && snapshotUrl !== candidate.snapshotUrl
                ? { ...candidate, snapshotUrl, snapshotError: undefined }
                : candidate;
    }

    return nextMain;
}

/** Restore per-variant module counts and metrics from captured placements when scalars were zeroed. */
function enrichCategoryLayoutVariantsFromBlob(
    variantsByCategory: Record<string, unknown[]>,
    layoutModulesLayoutByKey: Record<string, SavedRoofModulesLayout | undefined>,
    layoutProductionByKey?: Record<string, number | undefined>,
    layoutModulesByKey?: Record<string, number | undefined>,
    layoutMetricsByKey?: Record<string, unknown>,
): Record<string, unknown[]> {
    const out: Record<string, unknown[]> = {};
    for (const [category, rows] of Object.entries(variantsByCategory ?? {})) {
        out[category] = (rows ?? []).map((row) =>
            resolveAltLayoutVariantMetrics({
                category,
                variant: row as AltLayoutVariantRow,
                layoutModulesLayoutByKey,
                layoutProductionByKey,
                layoutModulesByKey,
                layoutMetricsByKey,
            }),
        );
    }
    return out;
}

type ApplyAiScenariosCacheArgs = {
    aiScenarios: AiScenariosCacheBlob | null | undefined;
    layoutSnapshots?: Record<string, unknown> | null;
    variantSnapshots?: Record<string, unknown> | null;
    treeCuttingScenarios?: Record<string, unknown> | null;
    aiResult?: string | null;
    aiDesignsDataSource?: 'cache' | 'db' | 'run';
    aiScenariosSectionVisible?: boolean;
    resultCardMinimized?: boolean;
};

/** Apply cached AI scenario metrics into the viewer store (catalog cards, tree scenarios, snapshots). */
export function applyAiScenariosCacheToViewerStore(args: ApplyAiScenariosCacheArgs): void {
    const {
        aiScenarios,
        layoutSnapshots,
        variantSnapshots,
        treeCuttingScenarios,
        aiResult,
        aiDesignsDataSource,
        aiScenariosSectionVisible = true,
        resultCardMinimized = false,
    } = args;

    const blob = normalizeAiScenariosBlob(aiScenarios);
    const normalizedMaps = normalizeLayoutMapsForCategoryCards(blob);
    const state = useViewerStore.getState() as any;
    const resolvedAiResult =
        typeof aiResult === 'string'
            ? aiResult
            : typeof state.aiResult === 'string'
              ? state.aiResult
              : '';

    const layoutModulesLayoutByKey = rebuildLayoutModulesLayoutByKeyFromBlob(blob);
    const categoryMainFromMetrics = rebuildCategoryMainLayoutFromMetrics(blob, layoutModulesLayoutByKey);
    const categoryMainLayoutByCategory = enrichCategoryMainLayoutsWithModulePlacements(
        {
            ...categoryMainFromMetrics,
            ...((blob.categoryMainLayoutByCategory ?? {}) as Record<string, CategoryLayoutCandidate>),
        },
        layoutModulesLayoutByKey,
    );
    const viewer = (state.sceneViewer ?? null) as {
        roofMeshArr?: Array<{ roofFaceId?: string; userData?: Record<string, unknown> }>;
    } | null;
    const enrichedLayoutModulesLayoutByKey = enrichModulesLayoutRecordWithViewer(
        viewer,
        layoutModulesLayoutByKey,
    );
    const enrichedCategoryMain = enrichCategoryMainLayoutsWithViewer(viewer, categoryMainLayoutByCategory);

    const parsedLayoutSnapshots = parseValidSnapshotMap(layoutSnapshots);
    const parsedVariantSnapshots = parseValidSnapshotMap(variantSnapshots);
    const resolvedLayoutSnapshots = (() => {
        if (layoutSnapshots === undefined) {
            return aiDesignsDataSource === 'db'
                ? {}
                : parseValidSnapshotMap((state.layoutSnapshotsByKey ?? {}) as Record<string, unknown>);
        }
        if (layoutSnapshots === null) {
            return aiDesignsDataSource === 'db'
                ? {}
                : parseValidSnapshotMap((state.layoutSnapshotsByKey ?? {}) as Record<string, unknown>);
        }
        return {
            ...parseValidSnapshotMap((state.layoutSnapshotsByKey ?? {}) as Record<string, unknown>),
            ...parsedLayoutSnapshots,
        };
    })();
    const resolvedVariantSnapshots = (() => {
        if (variantSnapshots === undefined) {
            return aiDesignsDataSource === 'db'
                ? {}
                : parseValidSnapshotMap((state.aiLayoutVariantSnapshots ?? {}) as Record<string, unknown>);
        }
        if (variantSnapshots === null) {
            return aiDesignsDataSource === 'db'
                ? {}
                : parseValidSnapshotMap((state.aiLayoutVariantSnapshots ?? {}) as Record<string, unknown>);
        }
        return {
            ...parseValidSnapshotMap((state.aiLayoutVariantSnapshots ?? {}) as Record<string, unknown>),
            ...parsedVariantSnapshots,
        };
    })();
    const aliasedLayoutSnapshots = aliasLayoutSnapshotKeys(
        resolvedLayoutSnapshots,
        blob.aiResolvedRoofCombinations,
    );
    const categoryMainWithSnapshots = rehydrateCategoryMainSnapshotsFromSidecarMaps({
        layoutSnapshotsByKey: aliasedLayoutSnapshots,
        variantSnapshots: resolvedVariantSnapshots,
        categoryMainLayoutByCategory: enrichedCategoryMain,
    });
    const rehydratedTreeCuttingScenarios = rehydrateTreeCutScenariosFromLayoutSnapshotMap(
        (treeCuttingScenarios ?? state.aiModuleFillingTreeScenariosByLayoutKey ?? {}) as Record<
            string,
            unknown[]
        >,
        aliasedLayoutSnapshots,
    );
    const enrichedCategoryVariants = enrichCategoryLayoutVariantsFromBlob(
        (blob.aiCategoryLayoutVariants ?? {}) as Record<string, unknown[]>,
        enrichedLayoutModulesLayoutByKey,
        normalizedMaps.layoutProductionByKey,
        normalizedMaps.layoutModulesByKey,
        normalizedMaps.layoutMetricsByKey,
    );

    useViewerStore.setState({
        layoutSnapshotsByKey: aliasedLayoutSnapshots,
        aiLayoutVariantSnapshots: resolvedVariantSnapshots,
        aiModuleFillingTreeScenariosByLayoutKey: rehydratedTreeCuttingScenarios,
        aiModuleFillingScenarios: blob.aiModuleFillingScenarios ?? [],
        aiModuleFillingLayoutVariants: blob.aiModuleFillingLayoutVariants ?? [],
        aiCategoryLayoutVariants: enrichedCategoryVariants,
        layoutProductionByKey: normalizedMaps.layoutProductionByKey,
        layoutModulesByKey: normalizedMaps.layoutModulesByKey,
        layoutMetricsByKey: normalizedMaps.layoutMetricsByKey,
        layoutModulesLayoutByKey: {
            ...(state.layoutModulesLayoutByKey ?? {}),
            ...enrichedLayoutModulesLayoutByKey,
        },
        categoryMainLayoutByCategory: categoryMainWithSnapshots,
        aiResolvedRoofCombinations: blob.aiResolvedRoofCombinations ?? [],
        aiResult: resolvedAiResult,
        aiScenariosSectionVisible,
        resultCardMinimized,
        ...(aiDesignsDataSource ? { aiDesignsDataSource } : {}),
    } as any);

    // Only re-derive winners for older cache rows that lack stored per-category mains.
    // Re-running on a complete cache row discards persisted winners when the 3D viewer
    // is not ready yet (common on refresh), which leaves only default maxfill visible.
    const viewerReady = Boolean(
        (useViewerStore.getState() as any).sceneViewer?.roofMeshArr?.length,
    );
    const shouldRecomputeCategoryMains =
        !hasStoredCategoryMainLayoutsInBlob(blob) &&
        viewerReady &&
        hasSimulatedLayoutCatalogData(blob.layoutProductionByKey, blob.layoutModulesByKey) &&
        Object.keys(blob.aiCategoryLayoutVariants ?? {}).length > 0;

    if (shouldRecomputeCategoryMains) {
        applyCategoryBestLayoutsAction({
            roofIdsByLayoutKey: (key) => resolveRoofIdsForLayoutKey(key),
            roofCombinationsFromAi: blob.aiResolvedRoofCombinations ?? [],
        });
    }
}

/** Load IndexedDB AI scenarios into the viewer store (fast-path cache hydration). */
export async function hydrateLocalAiResultCacheToViewerStore(sfId?: string | null): Promise<boolean> {
    const resolvedSfid = sfId ?? getSalesforceIdForAiCache();
    const local = await loadLocalAiResultCacheWithFallback(resolvedSfid);
    if (!local?.aiScenarios) return false;

    const blob = normalizeAiScenariosBlob(local.aiScenarios);
    if (!hasMeaningfulAiScenariosPayload(blob)) return false;

    applyAiScenariosCacheToViewerStore({
        aiScenarios: blob,
        layoutSnapshots: (local.layoutSnapshots as Record<string, unknown> | null | undefined) ?? null,
        variantSnapshots: (local.variantSnapshots as Record<string, unknown> | null | undefined) ?? null,
        treeCuttingScenarios: (local.treeCuttingScenarios as Record<string, unknown> | null) ?? {},
        aiResult: typeof local.aiResult === 'string' ? local.aiResult : null,
        aiDesignsDataSource: 'cache',
    });

    if (Array.isArray(local.aiWishlist)) {
        useOneButtonStore.getState().setSavedAIDesigns((local.aiWishlist as any) ?? []);
    }

    useViewerStore.setState({ aiDesignsDataSource: 'cache' } as any);
    return true;
}

/**
 * For legacy cache rows without stored category winners: re-run winner selection once the
 * 3D viewer roof meshes are available (after refresh).
 */
export function tryReapplyCategoryBestLayoutsFromStore(): boolean {
    const state = useViewerStore.getState() as any;
    if (hasHydratedAiLayoutCatalogInStore(state)) return false;
    if (!Boolean(state.sceneViewer?.roofMeshArr?.length)) return false;
    if (!hasSimulatedLayoutCatalogData(state.layoutProductionByKey, state.layoutModulesByKey)) {
        return false;
    }
    if (!Object.keys(state.aiCategoryLayoutVariants ?? {}).length) return false;

    applyCategoryBestLayoutsAction({
        roofIdsByLayoutKey: (key) => resolveRoofIdsForLayoutKey(key),
        roofCombinationsFromAi: state.aiResolvedRoofCombinations ?? [],
    });
    return hasHydratedAiLayoutCatalogInStore();
}

export function buildCachedMainLayoutCandidate(
    category: string,
    layoutProductionByKey: Record<string, number>,
    layoutModulesByKey: Record<string, number>,
    layoutMetricsByKey: Record<string, any>,
    layoutSnapshotsByKey: Record<string, string | undefined>,
    categoryMainLayoutByCategory: Record<string, CategoryLayoutCandidate> | undefined,
    roofIdsByLayoutKey: (key: string) => string[],
    layoutModulesLayoutByKey?: Record<string, SavedRoofModulesLayout | undefined>,
): CategoryLayoutCandidate | null {
    const catKey = getCategoryMainLayoutKey(category);
    const stored = categoryMainLayoutByCategory?.[category];
    const production = resolveCategoryNumericFromMaps(
        category,
        layoutProductionByKey,
        undefined,
    );
    const modules = resolveCategoryNumericFromMaps(category, layoutModulesByKey, undefined);

    if (!(production > 0) || !(modules > 0)) return null;

    const metrics = layoutMetricsByKey[catKey] ?? layoutMetricsByKey[category];
    const roofIds =
        (stored?.roofIds?.length ? stored.roofIds : null) ??
        (roofIdsByLayoutKey(category).length ? roofIdsByLayoutKey(category) : null) ??
        (roofIdsByLayoutKey(catKey).length ? roofIdsByLayoutKey(catKey) : null) ??
        [];

    if (!roofIds.length) return null;

    return {
        key: catKey,
        source: 'layoutKey',
        variantId: stored?.variantId,
        label: stored?.label,
        roofIds,
        dir: stored?.dir ?? null,
        minEff: stored?.minEff ?? null,
        production,
        modules,
        efficiency:
            typeof metrics?.efficiency === 'number' && Number.isFinite(metrics.efficiency)
                ? metrics.efficiency
                : stored?.efficiency ?? null,
        solarAccess:
            typeof metrics?.solarAccess === 'number' && Number.isFinite(metrics.solarAccess)
                ? metrics.solarAccess
                : stored?.solarAccess ?? null,
        multiplier:
            typeof metrics?.multiplier === 'number' && Number.isFinite(metrics.multiplier)
                ? metrics.multiplier
                : stored?.multiplier ?? null,
        snapshotUrl: layoutSnapshotsByKey[catKey] ?? layoutSnapshotsByKey[category] ?? stored?.snapshotUrl,
        treeCount: metrics?.treeCount ?? stored?.treeCount ?? null,
        roofCount: metrics?.roofCount ?? stored?.roofCount ?? null,
        modulesLayout: resolveModulesLayoutMatchingCount(
            layoutModulesLayoutByKey,
            [
                catKey,
                stored?.key,
                category,
                stored?.variantId
                    ? resolveAltLayoutVariantLayoutKey(category, { id: stored.variantId })
                    : undefined,
            ],
            modules,
            stored?.modulesLayout,
        ),
    };
}
